import { useMutation } from "@apollo/client/react";
import {
  CreateMultipleChoiceQuestion,
  CreateMultipleChoiceQuestionInputOption,
  CreateMultipleChoiceQuestionVariables,
  MultipleChoiceType,
} from "__generated__/types";
import { CancelOrSubmit, TextButton } from "common/buttons";
import { FieldError } from "common/components/forms/FieldError";
import { MarkdownField } from "common/components/forms/MarkdownField";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { InputField } from "common/components/InputField";
import { ListContainer } from "common/components/lists/ListContainer";
import { ListItem } from "common/components/lists/ListItem";
import { markdownInput } from "common/input-types";
import gql from "graphql-tag";
import * as React from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import formStyles from "./MultipleChoiceQuestionCreateForm.module.css";

const MUTATION = gql`
  mutation CreateMultipleChoiceQuestion(
    $input: CreateMultipleChoiceQuestionInput!
  ) {
    createMultipleChoiceQuestion(input: $input) {
      multipleChoiceQuestion {
        id
        title
        body {
          isMarkdown
          content
        }
        options {
          id
          title
          correct
        }
      }
    }
  }
`;

interface Props {
  multipleChoiceId: string;
  type: MultipleChoiceType;
  cancel(): void;
  complete(): void;
}

interface MultipleChoiceQuestionForm {
  body: string;
  question: string;
  options: CreateMultipleChoiceQuestionInputOption[] | null;
}

export const CreateMultipleChoiceQuestionForm: React.FC<
  React.PropsWithChildren<Props>
> = ({ multipleChoiceId, type, cancel, complete }) => {
  const methods = useForm<MultipleChoiceQuestionForm>({
    defaultValues: {
      options: [
        { correct: true, title: "" },
        { correct: false, title: "" },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "options",
  });

  const [mutate, { loading, error }] = useMutation<
    CreateMultipleChoiceQuestion,
    CreateMultipleChoiceQuestionVariables
  >(MUTATION);

  const onSubmit = async (data: MultipleChoiceQuestionForm) => {
    const { question, options, body } = data;

    const result = await mutate({
      variables: {
        input: {
          multipleChoiceId,
          title: question,
          options: options
            ? options.map((e) => ({
                title: e.title,
                correct: e.correct ?? true,
              }))
            : [],
          body: body ? markdownInput(body) : null,
        },
      },
    });

    if (result.data) {
      complete();
    }
  };

  return (
    // TODO: Handle minimum number of options in question
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className={formStyles.container}
      >
        <header className={formStyles.header}>
          <InputField
            id="question"
            placeholder={
              type === MultipleChoiceType.STANDARD
                ? "Skriv inn spørsmålet her.."
                : "Skriv inn navn på grupperingskategori.."
            }
            kind="large"
            register={methods.register("question", {
              required: "Vennligst legg til et spørsmål",
              minLength: 1,
            })}
          />
          <FieldError name="title" />
        </header>
        <section className={formStyles.inner}>
          <ListContainer noMargin>
            {fields.map((item, index) => {
              const checkboxId = `options.${index}.correct` as const;
              const titleId = `options.${index}.title` as const;
              return (
                <ListItem key={item.id} className={formStyles.optionsContainer}>
                  <input
                    type="checkbox"
                    id={checkboxId}
                    defaultChecked={type === MultipleChoiceType.GROUPING}
                    disabled={type === MultipleChoiceType.GROUPING}
                    {...methods.register(checkboxId)}
                  />
                  <InputField
                    id={titleId}
                    kind="normal"
                    placeholder="Skriv inn alternativ.."
                    aria-label="Alternativ"
                    register={methods.register(titleId, {
                      required: "Svaralternativ kan ikke være tomt",
                      minLength: 1,
                    })}
                  />
                  <TextButton
                    kind="secondary"
                    onClick={() => {
                      remove(index);
                    }}
                  >
                    Slett
                  </TextButton>
                  <FieldError name={`options[${index}].title`} />
                </ListItem>
              );
            })}
          </ListContainer>

          <p className={formStyles.addAnother}>
            <TextButton
              textSize="small"
              onClick={() => {
                append({ correct: false, title: "" });
              }}
              kind="secondary"
            >
              + Legg til alternativ
            </TextButton>
          </p>

          {type == MultipleChoiceType.STANDARD && (
            <MarkdownField<MultipleChoiceQuestionForm>
              name="body"
              title="Tilleggsinformasjon til spørsmålet"
              defaultValue=""
            />
          )}

          <MutationLoadingOrError loading={loading} error={error} />

          <CancelOrSubmit onCancel={cancel} isLoading={loading} />
        </section>
      </form>
    </FormProvider>
  );
};
